import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";

import type { PageKey } from "@/config/pages";
import { getPagePath } from "@/config/pages";
import { isSupportedLanguage } from "@/i18n/routing";

/**
 * Builds a language-prefixed path to one of the app's pages.
 *
 * The same few lines: read the language, check it is one we support, prefix
 * the path or fall back to the bare one: were written out at every call site
 * that navigates. Getting it wrong sends a French user to an unprefixed URL
 * that `I18nRouter` does not match, which is a blank page rather than a
 * redirect, so it is worth having exactly one copy.
 *
 * The language comes from the ROUTE first and from i18next only as a fallback:
 * the URL is what the router will match, and a component rendered outside a
 * `/:lang` route has no param to read. The two agree in practice:
 * `useSyncLanguage` keeps them in step, but when they disagree the URL wins,
 * because the URL is what we are building.
 */
const useLocalizedPath = () => {
  const { lang } = useParams<{ lang: string }>();
  const { i18n } = useTranslation();

  // The presence check comes first on purpose: a component rendered outside a
  // `/:lang` route has no param at all, and `undefined` must fall through to
  // the active language rather than be asked whether it is supported.
  const fromRoute = lang && isSupportedLanguage(lang) ? lang : null;
  const active = i18n.resolvedLanguage;
  const language =
    fromRoute ?? (active && isSupportedLanguage(active) ? active : null);

  return (page: PageKey, params?: Record<string, number | string>): string =>
    language
      ? getPagePath(page, language, params)
      : getPagePath(page, undefined, params);
};

export default useLocalizedPath;
